import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { JwtAuthService } from '../../../shared/services/auth/jwt-auth.service';

export interface WhatsAppConfig {
  instanceName: string;
  apiUrl: string;
  apiKey: string;
  /** Telefone do aparelho que vai parear. Opcional: sem ele a conexão volta a ser por QR Code. */
  number?: string;
}

export interface InstanceStatus {
  instance: {
    instanceName: string;
    status: 'open' | 'close' | 'connecting';
  };
  qrcode?: {
    base64: string;
    code: string;
  };
}

export interface ConnectionResponse {
  instance: {
    instanceName: string;
    status: string;
  };
  hash?: {
    apikey: string;
  };
  qrcode?: {
    base64: string;
    code: string;
    /** Código de pareamento, só vem quando a instância foi criada/conectada com número. */
    pairingCode?: string;
  };
  base64?: string; // QR Code base64 direto na resposta
  /** Código de pareamento no nível raiz — é onde o /instance/connect o devolve. */
  pairingCode?: string;
}

/** Resposta de GET /evolution/instance/minha. */
/** Quem atende por uma instância, quem pode passar a atender, e o teto. */
export interface UsuariosDaInstancia {
  vinculados: { id: number; usuarioKey: string; vinculadoEm?: string; vinculadoPor?: string }[];
  candidatos: string[];
  limite: number;
}

/** Volume de um mês, do ponto de vista da cobrança da Meta. */
export interface MesEstimativa {
  mes: string;
  recebidas: number;
  enviadas: number;
  /** Gratuitas na API oficial: resposta dentro da janela de 24h. */
  enviadasNaJanela: number;
  /** O custo: fora da janela, só sai com template pago. */
  enviadasForaDaJanela: number;
  /** Quanto desse custo é automação do próprio sistema. */
  foraAutomaticas: number;
  atendimentosGratuitos: number;
}

/** Quanta mídia entra e quanto dela já está guardada — base da decisão de arquivar ou não. */
export interface MidiasEstimativa {
  porTipo: { tipo: string; total: number; guardadas: number; naoAbertas: number }[];
  total: number;
  guardadas: number;
  naoAbertas: number;
  bytesGuardados: number;
  mediaPorArquivo: number;
  /** Quanto ocuparia guardar tudo do período, ao tamanho médio observado. */
  projecaoBytes: number;
}

export interface EstimativaOficial {
  desde: string;
  meses: MesEstimativa[];
  total: MesEstimativa;
  midias?: MidiasEstimativa;
  observacao: string;
}

export interface MinhaInstancia {
  /** A instância é o número da empresa, compartilhado por quem não tem número próprio. */
  padraoFilial?: boolean;
  /** Login de quem pareou a instância. */
  dono?: string | null;
  instanceName: string;
  /** true quando o nome veio do formato antigo (o login) e a instância já está pareada. */
  legado: boolean;
}

/** Resposta de GET /evolution/conversa/lead-form. */
export interface StatusLeadForm {
  /** true quando há URL de formulário configurada. Sem ela, nada é enviado. */
  configurado: boolean;
  /** Envio automático do link para número desconhecido. */
  autoEnviar: boolean;
  validadeDias: number;
  url: string | null;
  /** Auto-envio ligado sem URL: configuração pela metade, não envia nada. */
  inconsistente: boolean;
  /** Aviso de "aguarde, um atendente vai responder" para número não identificado. */
  aguardeEnviar: boolean;
  aguardeTexto: string;
  /** true quando o valor veio da instância; false quando é o padrão do deploy. */
  autoEnviarDaInstancia: boolean;
  autoEnviarPadraoDeploy: boolean;
  aguardeDaInstancia: boolean;
  aguardePadraoDeploy: boolean;
  /** Aviso ao vendedor quando um lead conclui o autocadastro. */
  avisarVendedor: boolean;
  avisarVendedorDaInstancia: boolean;
  avisarVendedorPadraoDeploy: boolean;
  /** Destino do aviso; em branco na tela significa o próprio número da instância. */
  avisoVendedorNumero: string | null;
  /** Cartão no funil quando cliente já cadastrado escreve. */
  /** Esta instância é o número da empresa, usado por quem não tem o próprio. */
  padraoFilial: boolean;
  /** Horário de atendimento, "HH:mm"; os dois em branco = fechado naquele dia. */
  horarioSemanaDe: string;
  horarioSemanaAte: string;
  horarioSabadoDe: string;
  horarioSabadoAte: string;
  horarioDomingoDe: string;
  horarioDomingoAte: string;
  /** Data e hora do servidor no fuso do atendimento, para conferir a configuração. */
  horarioAgora?: string;
  atendimentoFechadoAgora?: boolean;
  /** "amanhã a partir das 8h", quando fechado agora. */
  atendimentoVolta?: string | null;
  /** Dias sem cartão novo para o mesmo cliente depois de ganho ou perda. */
  carenciaDias: number;
  carenciaDiasDaInstancia: boolean;
  carenciaDiasPadraoDeploy: number;
}

/** Resposta de GET /evolution/instance/conferir. */
export interface ConferenciaInstancia {
  instanceName: string;
  registrada: boolean;
  legado: boolean;
  /** Id da instância no banco da Evolution; é ele que muda quando a instância é recriada. */
  instanceId: string | null;
  evolutionDisponivel: boolean;
  existeNaEvolution?: boolean;
  instanceIdEvolution?: string | null;
  /** true quando o id mudou: mesmo nome, outro pareamento de WhatsApp. */
  recriada?: boolean;
}

/** Resposta de POST /evolution/instance/backfill. */
export interface BackfillInstancias {
  registradas: number;
  instancias: string[];
}

@Injectable({
  providedIn: 'root'
})
export class WhatsAppConfigService {
  
  constructor(private http: HttpClient, private jwtAuthService: JwtAuthService) {}

  /**
   * Salva ou atualiza a configuração da Evolution API
   */
  saveConfiguration(config: WhatsAppConfig): Observable<any> {
    console.log('Salvando configuração da Evolution API:', config);
    console.log('API URL:', `${this.jwtAuthService.apiUrl}/evolution/instance/create`);

    return this.http.post(`${this.jwtAuthService.apiUrl}/evolution/instance/create`, config)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Obtém a configuração atual da Evolution API
   */
  getConfiguration(): Observable<WhatsAppConfig> {
    return this.http.get<WhatsAppConfig>(`${this.jwtAuthService.apiUrl}/evolution/config`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Testa a conexão com a API Evolution
   */
  testConnection(config: WhatsAppConfig): Observable<boolean> {
    const headers = this.createHeaders(config.apiKey);
    return this.http.get(`${this.jwtAuthService.apiUrl}/evolution/health`, { headers })
      .pipe(
        map(() => true),
        catchError(() => throwError(new Error('Falha ao conectar com a API')))
      );
  }

  /**
   * Cria uma nova instância no Evolution API
   */
  createInstance(instanceName: string, number?: string): Observable<ConnectionResponse> {
    console.log('Criando nova instância:', instanceName);
    return this.http.post<ConnectionResponse>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/create`,
      number ? { instanceName, number } : { instanceName }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Obtém o QR Code para conectar uma instância
   * Este endpoint retorna o QR code que precisa ser escaneado para conectar a conta do WhatsApp
   */
  connectInstance(instanceName: string, number?: string): Observable<ConnectionResponse> {
    const params = number ? { params: { number } } : {};
    return this.http.get<ConnectionResponse>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/connect/${instanceName}`,
      params
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Obtém o status de uma instância
   */
  getInstanceStatus(instanceName: string): Observable<InstanceStatus> {
    return this.http.get<InstanceStatus>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/connectionState/${instanceName}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Obtém o QR Code de uma instância
   */
  getQrCode(instanceName: string): Observable<string> {
    return this.http.get<any>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/connect/${instanceName}`
    ).pipe(
      map(response => {
        if (response.qrcode && response.qrcode.base64) {
          return response.qrcode.base64;
        }
        throw new Error('QR Code não disponível');
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Desconecta (faz logout) de uma instância
   */
  disconnectInstance(instanceName: string): Observable<any> {
    // O endpoint no backend expõe POST /instance/logout/{instanceName}
    return this.http.post(
      `${this.jwtAuthService.apiUrl}/evolution/instance/logout/${instanceName}`,
      {}
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Reinicia uma instância
   */
  restartInstance(instanceName: string): Observable<any> {
    return this.http.put(
      `${this.jwtAuthService.apiUrl}/evolution/instance/restart/${instanceName}`,
      {}
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Deleta uma instância
   */
  deleteInstance(instanceName: string): Observable<any> {
    return this.http.delete(
      `${this.jwtAuthService.apiUrl}/evolution/instance/delete/${instanceName}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Instância registrada para o usuário logado.
   *
   * Fonte da verdade do nome: o backend resolve pelo registro em EvolutionInstance, em vez de a
   * tela montar o nome a partir do login. Registra na primeira chamada — se já existe instância
   * pareada com o nome antigo, ela é preservada (legado = true) e ninguém precisa reler o QR Code.
   */
  minhaInstancia(): Observable<MinhaInstancia> {
    return this.http.get<MinhaInstancia>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/minha`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Contas que atendem por esta instância, além do dono, e quem mais pode ser vinculado.
   *
   * O vínculo era um só — instância de uma pessoa, ou número de toda a filial. Isto descreve o meio
   * do caminho: um número atendido por um time.
   */
  usuariosDaInstancia(instanceName: string): Observable<UsuariosDaInstancia> {
    return this.http.get<UsuariosDaInstancia>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/${encodeURIComponent(instanceName)}/usuarios`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Volume por mês no recorte que a Meta cobra.
   *
   * Percorre meses de histórico, então é sob demanda — nunca ao abrir a tela.
   */
  estimativaOficial(meses: number): Observable<EstimativaOficial> {
    const params = new HttpParams().set('meses', String(meses));
    return this.http.get<EstimativaOficial>(
      `${this.jwtAuthService.apiUrl}/conversa/estimativa-oficial`, { params }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /** A conta precisa ser usuária da mesma filial da instância; o backend recusa se não for. */
  vincularUsuario(instanceName: string, usuarioKey: string): Observable<any> {
    const params = new HttpParams().set('usuarioKey', usuarioKey);
    return this.http.post(
      `${this.jwtAuthService.apiUrl}/evolution/instance/${encodeURIComponent(instanceName)}/usuarios`,
      null, { params }
    ).pipe(
      catchError(this.handleError)
    );
  }

  desvincularUsuario(instanceName: string, usuarioKey: string): Observable<any> {
    const params = new HttpParams().set('usuarioKey', usuarioKey);
    return this.http.delete(
      `${this.jwtAuthService.apiUrl}/evolution/instance/${encodeURIComponent(instanceName)}/usuarios`,
      { params }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Situação do formulário de autocadastro (somente root). As chaves podem vir do environment do
   * container, então este é o único jeito de conferir o estado sem acesso ao servidor.
   */
  statusLeadForm(): Observable<StatusLeadForm> {
    return this.http.get<StatusLeadForm>(
      `${this.jwtAuthService.apiUrl}/evolution/conversa/lead-form`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Confere o registro local contra a Evolution: completa ou corrige o instanceId e avisa se a
   * instância foi recriada lá (mesmo nome, outro pareamento).
   */
  conferirInstancia(): Observable<ConferenciaInstancia> {
    return this.http.get<ConferenciaInstancia>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/conferir`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Grava as respostas automáticas da instância. Vale sobre a property do deploy.
   */
  salvarLeadForm(config: {
    autoEnviar: boolean | null; aguardeEnviar: boolean | null; aguardeTexto: string | null;
    avisarVendedor: boolean | null; avisoVendedorNumero: string | null;
    padraoFilial: boolean | null;
    carenciaDias: number | null;
    horarioSemanaDe?: string | null; horarioSemanaAte?: string | null;
    horarioSabadoDe?: string | null; horarioSabadoAte?: string | null;
    horarioDomingoDe?: string | null; horarioDomingoAte?: string | null;
  }):
    Observable<StatusLeadForm> {
    return this.http.put<StatusLeadForm>(
      `${this.jwtAuthService.apiUrl}/evolution/conversa/lead-form`, config
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Registra no banco as instâncias que já existem na Evolution e pertencem a usuários deste
   * banco, preservando o nome atual delas. Exige ROLE_ROOT ou ROLE_ADMIN_WRITE.
   */
  backfillInstancias(): Observable<BackfillInstancias> {
    return this.http.post<BackfillInstancias>(
      `${this.jwtAuthService.apiUrl}/evolution/instance/backfill`,
      {}
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Assina os eventos de webhook para a instância do usuário logado. Instância criada antes dessa
   * funcionalidade nasceu sem webhook — é este endpoint que evita ter de recriá-la.
   */
  registrarWebhook(): Observable<any> {
    return this.http.post(
      `${this.jwtAuthService.apiUrl}/evolution/instance/webhook`,
      {}
    ).pipe(
      catchError(this.handleError)
    );
  }

  /** Webhook que a Evolution tem registrado para a instância do usuário. */
  consultarWebhook(): Observable<any> {
    return this.http.get(
      `${this.jwtAuthService.apiUrl}/evolution/instance/webhook`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Envia uma mensagem de texto pela instância — usado no teste de ponta a ponta da configuração.
   */
  enviarMensagemTeste(instanceName: string, numero: string, texto: string): Observable<any> {
    return this.http.post(
      `${this.jwtAuthService.apiUrl}/evolution/message/sendText/${instanceName}`,
      { number: numero, text: texto }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Verifica se a instância está conectada
   */
  isInstanceConnected(instanceName: string): Observable<boolean> {
    return this.getInstanceStatus(instanceName).pipe(
      map(status => status.instance.status === 'open'),
      catchError(() => throwError(new Error('Erro ao verificar status')))
    );
  }

  /**
   * Lista todas as instâncias configuradas
   */
  listInstances(): Observable<any[]> {
    return this.http.get<any[]>(`${this.jwtAuthService.apiUrl}/evolution/instance/fetchInstances`)
      .pipe(
        catchError(this.handleError)
      );
  }

  
  
  /**
   * Busca uma instância específica pelo nome
   */
  fetchInstance(instanceName: string): Observable<any> {
    
    return this.http.get<any>(`${this.jwtAuthService.apiUrl}/evolution/instance/fetchInstances/${instanceName}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Cria headers HTTP com a API Key
   */
  private createHeaders(apiKey: string): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'apikey': apiKey
    });
  }

  /**
   * Tratamento de erros HTTP
   */
  private handleError(error: any): Observable<never> {
    let errorMessage = 'Erro desconhecido';
    
    if (error.error instanceof ErrorEvent) {
      // Erro do lado do cliente
      errorMessage = `Erro: ${error.error.message}`;
    } else {
      // Erro do lado do servidor
      // O backend responde { erro: true, mensagem: '...' }: sem ler "mensagem", um 400 legítimo
      // (número de telefone inválido, por exemplo) chegava ao usuário como "Erro 400" e mais nada.
      if (error.error && error.error.mensagem) {
        errorMessage = error.error.mensagem;
      } else if (error.status === 0) {
        errorMessage = 'Não foi possível conectar ao servidor';
      } else if (error.status === 401) {
        errorMessage = 'Não autorizado - verifique a API Key';
      } else if (error.status === 404) {
        errorMessage = 'Recurso não encontrado';
      } else if (error.status === 500) {
        errorMessage = 'Erro interno do servidor';
      } else if (error.error && error.error.message) {
        errorMessage = error.error.message;
      } else {
        errorMessage = `Erro ${error.status}: ${error.message}`;
      }
    }

    console.error('Erro na requisição:', error);
    // throwError com factory e RxJS 7+; neste projeto (6.6.7) o valor e emitido como esta, entao
    // uma arrow function chegaria ao subscriber no lugar do Error -- e a tela mostrava o corpo
    // minificado da funcao ("()=>new Error(b)") em vez da mensagem.
    return throwError(new Error(errorMessage));
  }
}
